import React, { useState, useEffect } from 'react';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from './ui/card';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from './ui/table';
import { Badge } from './ui/badge';
import { Button } from './ui/button';
import { Checkbox } from './ui/checkbox';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { RefreshCw, Filter, CheckSquare, Square } from 'lucide-react';
import { toast } from 'sonner';
import { getAuthenticatedAxios, handleAuthError } from '../utils/api';

const WalletPriorityList = () => {
  const [wallets, setWallets] = useState([]);
  const [groups, setGroups] = useState([]);
  const [selectedGroups, setSelectedGroups] = useState([]);
  const [selectedWallets, setSelectedWallets] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filterGroup, setFilterGroup] = useState('');
  const [searchQuery, setSearchQuery] = useState('');

  useEffect(() => {
    loadGroups();
    loadWallets();
  // eslint-disable-next-line
  }, []); // Загружаем один раз при монтировании компонента

  const loadGroups = async () => {
    try {
      const api = getAuthenticatedAxios();
      const response = await api.get('/api/wallets/groups');
      setGroups(response.data.groups || []);
    } catch (error) {
      console.error('Error loading groups:', error);
      if (!handleAuthError(error, toast)) {
        console.error('Error loading groups:', error);
      }
    }
  };

  const loadWallets = async () => {
    setLoading(true);
    try {
      const api = getAuthenticatedAxios();
      
      // Загружаем ВСЕ кошельки (без фильтра по группе на API уровне)
      const response = await api.get('/api/wallets/list', {});
      const walletsList = response.data.wallets || [];
      
      console.log(`Loaded ${walletsList.length} wallets from API`);
      
      // Загружаем приоритетные кошельки для получения warmup stats
      try {
        const prioritizedResponse = await api.get('/api/warmup/wallets/prioritized', {
          params: { limit: 10000 } // Большой лимит чтобы получить все
        });
        if (prioritizedResponse.data && prioritizedResponse.data.success) {
          const prioritizedMap = new Map();
          // Создаем map с обоими форматами адресов для каждого кошелька
          prioritizedResponse.data.wallets.forEach(w => {
            const addr = w.address;
            prioritizedMap.set(addr, w);
            // Добавляем альтернативный формат
            if (addr.startsWith('UQ')) {
              prioritizedMap.set('EQ' + addr.substring(2), w);
            } else if (addr.startsWith('EQ')) {
              prioritizedMap.set('UQ' + addr.substring(2), w);
            }
          });
          
          console.log(`Loaded ${prioritizedResponse.data.wallets.length} prioritized wallets (${prioritizedMap.size} entries in map)`);
          
          // Объединяем данные
          const enrichedWallets = walletsList.map(wallet => {
            // Пробуем найти по точному совпадению
            let prioritized = prioritizedMap.get(wallet.address);
            
            // Если не нашли, пробуем альтернативный формат
            if (!prioritized && wallet.address) {
              if (wallet.address.startsWith('UQ')) {
                prioritized = prioritizedMap.get('EQ' + wallet.address.substring(2));
              } else if (wallet.address.startsWith('EQ')) {
                prioritized = prioritizedMap.get('UQ' + wallet.address.substring(2));
              }
            }
            
            if (prioritized) {
              return {
                ...wallet,
                priority: prioritized.priority || 1000,
                warmup_stats: prioritized.warmup_stats || wallet.warmup_stats || {
                  successful_swaps_count: 0,
                  failed_swaps_count: 0,
                  total_swaps_count: 0,
                  last_warmup_timestamp: null
                },
                successful_swaps_count: prioritized.successful_swaps_count || 0,
                failed_swaps_count: prioritized.failed_swaps_count || 0
              };
            }
            return {
              ...wallet,
              priority: 1000, // Default priority for wallets without warmup stats
              warmup_stats: wallet.warmup_stats || {
                successful_swaps_count: 0,
                failed_swaps_count: 0,
                total_swaps_count: 0,
                last_warmup_timestamp: null
              }
            };
          });
          
          console.log(`Enriched ${enrichedWallets.length} wallets with warmup stats`);
          setWallets(enrichedWallets);
        } else {
          console.log('No prioritized wallets data, using basic wallet list');
          // Если нет данных о приоритетах, просто используем список кошельков
          const walletsWithDefaults = walletsList.map(wallet => ({
            ...wallet,
            priority: 1000,
            warmup_stats: wallet.warmup_stats || {
              successful_swaps_count: 0,
              failed_swaps_count: 0,
              total_swaps_count: 0,
              last_warmup_timestamp: null
            }
          }));
          setWallets(walletsWithDefaults);
        }
      } catch (error) {
        console.error('Error loading prioritized wallets:', error);
        // Если ошибка при загрузке приоритетов, все равно показываем кошельки
        const walletsWithDefaults = walletsList.map(wallet => ({
          ...wallet,
          priority: 1000,
          warmup_stats: wallet.warmup_stats || {
            successful_swaps_count: 0,
            failed_swaps_count: 0,
            total_swaps_count: 0,
            last_warmup_timestamp: null
          }
        }));
        setWallets(walletsWithDefaults);
      }
    } catch (error) {
      console.error('Error loading wallets:', error);
      if (!handleAuthError(error, toast)) {
        toast.error(`Ошибка загрузки кошельков: ${error.response?.data?.detail || error.message}`);
      }
      setWallets([]);
    } finally {
      setLoading(false);
    }
  };

  const handleResetStats = async (walletAddress) => {
    if (!window.confirm('Вы уверены, что хотите сбросить статистику для этого кошелька?')) {
      return;
    }
    try {
      const api = getAuthenticatedAxios();
      await api.post(`/api/warmup/wallets/reset-stats?wallet_address=${encodeURIComponent(walletAddress)}`);
      toast.success('Статистика сброшена');
      loadWallets();
    } catch (error) {
      if (!handleAuthError(error, toast)) {
        toast.error(`Failed to reset stats: ${error.response?.data?.detail || error.message}`);
      }
    }
  };

  // Фильтрация кошельков
  const filteredWallets = wallets.filter(wallet => {
    // Фильтр по группе
    if (filterGroup && (wallet.group_name || 'No Group') !== filterGroup) {
      return false;
    }
    
    // Фильтр по выбранным группам
    if (selectedGroups.length > 0) {
      const walletGroup = wallet.group_name || 'No Group';
      if (!selectedGroups.includes(walletGroup)) {
        return false;
      }
    }
    
    // Поиск по адресу
    if (searchQuery) {
      const query = searchQuery.toLowerCase();
      if (!wallet.address.toLowerCase().includes(query)) {
        return false;
      }
    }
    
    return true;
  });

  const toggleWalletSelection = (address) => {
    setSelectedWallets(prev => 
      prev.includes(address) 
        ? prev.filter(a => a !== address)
        : [...prev, address]
    );
  };

  const selectAllWallets = () => {
    setSelectedWallets(filteredWallets.map(w => w.address));
  };

  const deselectAllWallets = () => {
    setSelectedWallets([]);
  };

  const selectGroup = (groupName) => {
    const groupWallets = filteredWallets
      .filter(w => (w.group_name || 'No Group') === groupName)
      .map(w => w.address);
    setSelectedWallets(groupWallets);
    toast.success(`Выбрано ${groupWallets.length} кошельков из группы ${groupName}`);
  };

  const toggleGroupSelection = (groupName) => {
    setSelectedGroups(prev => 
      prev.includes(groupName)
        ? prev.filter(g => g !== groupName)
        : [...prev, groupName]
    );
  };

  // Группировка кошельков для отображения статистики (используем все кошельки, не только отфильтрованные)
  const walletGroupsStats = groups.map(group => {
    const groupWallets = wallets.filter(w => (w.group_name || 'No Group') === group.group_name);
    const totalTON = groupWallets.reduce((sum, w) => {
      const balance = parseFloat(w.ton_balance || 0) / 1e9;
      return sum + balance;
    }, 0);
    return {
      ...group,
      walletCount: groupWallets.length,
      totalTON
    };
  });

  if (loading) {
    return (
      <Card>
        <CardContent className="p-6">
          <div className="text-center">Загрузка кошельков...</div>
        </CardContent>
      </Card>
    );
  }

  return (
    <div className="space-y-4">
      {/* Фильтры и управление */}
      <Card>
        <CardHeader>
          <CardTitle>Управление кошельками для Warmup</CardTitle>
          <CardDescription>Выберите группы и кошельки для разогрева</CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          {/* Поиск */}
          <div>
            <Label htmlFor="search">Поиск по адресу</Label>
            <Input
              id="search"
              placeholder="Введите адрес кошелька..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
            />
          </div>

          {/* Выбор групп */}
          <div>
            <Label>Группы кошельков</Label>
            <div className="flex flex-wrap gap-2 mt-2">
              <Button
                variant={filterGroup === '' ? 'default' : 'outline'}
                size="sm"
                onClick={() => setFilterGroup('')}
              >
                Все группы
              </Button>
              {groups.map((group) => (
                <Button
                  key={group.group_name}
                  variant={filterGroup === group.group_name ? 'default' : 'outline'}
                  size="sm"
                  onClick={() => setFilterGroup(filterGroup === group.group_name ? '' : group.group_name)}
                >
                  {group.group_name} ({group.wallet_count || 0})
                </Button>
              ))}
            </div>
          </div>

          {/* Действия с выбранными */}
          <div className="flex items-center gap-2">
            <Button
              variant="outline"
              size="sm"
              onClick={selectAllWallets}
            >
              <CheckSquare className="h-4 w-4 mr-2" />
              Выбрать все
            </Button>
            <Button
              variant="outline"
              size="sm"
              onClick={deselectAllWallets}
            >
              <Square className="h-4 w-4 mr-2" />
              Снять выбор
            </Button>
            <Button
              variant="outline"
              size="sm"
              onClick={loadWallets}
            >
              <RefreshCw className="h-4 w-4 mr-2" />
              Обновить
            </Button>
            <div className="ml-auto text-sm text-gray-600">
              Выбрано: {selectedWallets.length} / {filteredWallets.length}
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Список кошельков */}
      <Card>
        <CardHeader>
          <CardTitle>Список кошельков ({filteredWallets.length})</CardTitle>
        </CardHeader>
        <CardContent>
          <div className="overflow-x-auto">
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead className="w-12">
                    <Checkbox
                      checked={selectedWallets.length === filteredWallets.length && filteredWallets.length > 0}
                      onCheckedChange={(checked) => checked ? selectAllWallets() : deselectAllWallets()}
                    />
                  </TableHead>
                  <TableHead>Адрес</TableHead>
                  <TableHead>Группа</TableHead>
                  <TableHead>Баланс (TON)</TableHead>
                  <TableHead>Приоритет</TableHead>
                  <TableHead>Успешных</TableHead>
                  <TableHead>Ошибок</TableHead>
                  <TableHead>Последний разогрев</TableHead>
                  <TableHead>Действия</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {filteredWallets.map((wallet) => {
                  const isSelected = selectedWallets.includes(wallet.address);
                  // Баланс в нанотонах, конвертируем в TON
                  const tonBalanceRaw = parseFloat(wallet.ton_balance || 0);
                  const tonBalance = tonBalanceRaw / 1e9;
                  const warmupStats = wallet.warmup_stats || {};
                  
                  return (
                    <TableRow key={wallet.address} className={isSelected ? 'bg-blue-50' : ''}>
                      <TableCell>
                        <Checkbox
                          checked={isSelected}
                          onCheckedChange={() => toggleWalletSelection(wallet.address)}
                        />
                      </TableCell>
                      <TableCell className="font-mono text-xs">
                        <div className="flex flex-col gap-1">
                          <span className="cursor-pointer hover:underline" onClick={() => {
                            navigator.clipboard.writeText(wallet.address);
                            toast.success('Адрес скопирован');
                          }}>
                            {wallet.address.slice(0, 10)}...{wallet.address.slice(-8)}
                          </span>
                        </div>
                      </TableCell>
                      <TableCell>
                        <Badge variant="outline">
                          {wallet.group_name || 'No Group'}
                        </Badge>
                      </TableCell>
                      <TableCell>{tonBalance.toFixed(4)}</TableCell>
                      <TableCell>
                        <Badge variant="outline">
                          {wallet.priority ? wallet.priority.toFixed(2) : '1000.00'}
                        </Badge>
                      </TableCell>
                      <TableCell>
                        <Badge variant="default">
                          {warmupStats.successful_swaps_count || 0}
                        </Badge>
                      </TableCell>
                      <TableCell>
                        <Badge variant="destructive">
                          {warmupStats.failed_swaps_count || 0}
                        </Badge>
                      </TableCell>
                      <TableCell className="text-xs">
                        {warmupStats.last_warmup_timestamp 
                          ? new Date(warmupStats.last_warmup_timestamp).toLocaleString('ru-RU')
                          : 'Никогда'}
                      </TableCell>
                      <TableCell>
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={() => handleResetStats(wallet.address)}
                        >
                          Сбросить
                        </Button>
                      </TableCell>
                    </TableRow>
                  );
                })}
              </TableBody>
            </Table>
          </div>
          {filteredWallets.length === 0 && (
            <div className="text-center py-8 text-gray-500">
              {searchQuery || filterGroup 
                ? 'Кошельки не найдены по заданным фильтрам'
                : 'Нет кошельков в системе'}
            </div>
          )}
        </CardContent>
      </Card>

      {/* Группы кошельков */}
      {walletGroupsStats.length > 0 && (
        <Card>
          <CardHeader>
            <CardTitle>Группы кошельков</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
              {walletGroupsStats.map((group) => (
                <Card key={group.group_name}>
                  <CardHeader className="pb-3">
                    <div className="flex items-center justify-between">
                      <CardTitle className="text-sm">{group.group_name}</CardTitle>
                      <Badge variant="outline">{group.walletCount}</Badge>
                    </div>
                  </CardHeader>
                  <CardContent>
                    <div className="space-y-2">
                      <div className="text-sm text-gray-600">
                        Кошельков: {group.walletCount}
                      </div>
                      <div className="text-sm text-gray-600">
                        Всего TON: {group.totalTON.toFixed(4)}
                      </div>
                      <Button
                        variant="outline"
                        size="sm"
                        className="w-full"
                        onClick={() => {
                          setFilterGroup(group.group_name);
                          selectGroup(group.group_name);
                        }}
                      >
                        Выбрать группу
                      </Button>
                    </div>
                  </CardContent>
                </Card>
              ))}
            </div>
          </CardContent>
        </Card>
      )}
    </div>
  );
};

export default WalletPriorityList;

